import { useEffect, useState } from 'react';

import { useContacts } from '../../client-queries';
import { isB2b } from '../../utils';
import { useProfile } from '../use-profile/use-profile';

const resolveContactPermissions = (permission: { showprice?: boolean; canorder?: boolean } | undefined | null) => ({
  showprice: permission?.showprice ?? true,
  canorder: permission?.canorder ?? true,
});

// [39787] RBAC is B2B-only. On the colora (B2C) build this hook returns undefined:
// no permission values exist, no contacts fetch runs, and consumers must fall back
// to their pre-RBAC behavior instead of acting on any permission value.
//
// [44843] Resolution contract for the nav loader. `loading` must be true from the
// very first paint (SSR and hydration alike) and stay true until the client actually
// knows the answer, because the server never does: the auth session lives in
// localStorage, so SSR always sees isLoggedIn=false while a logged-in client
// hydrates with isLoggedIn=true. Without the pre-resolution pin the server renders
// icons and the client renders the skeleton — a guaranteed hydration mismatch.
// After mount (isResolved) the answer is real:
//   - logged out → undefined (contacts query is disabled while logged out and v4
//     keeps isLoading=true forever on disabled queries — never trust it there);
//   - logged in → the contacts result, with loading=isLoading (fail-open T18 rules).
export const useContactPermissions = (
  locale: string,
): { showprice: boolean; canorder: boolean; loading: boolean } | undefined => {
  const { data: contacts, isLoading } = useContacts(locale, { enabled: isB2b });
  const { data: profile, isLoggedIn } = useProfile();
  const [isResolved, setIsResolved] = useState(false);

  useEffect(() => setIsResolved(true), []);

  if (!isB2b) {
    return undefined;
  }

  if (!isResolved) {
    return { showprice: true, canorder: true, loading: true };
  }

  if (!isLoggedIn) {
    return undefined;
  }

  const contact = contacts?.find(c => c.id === profile?.extension_ContactPersonId);

  const { showprice, canorder } = resolveContactPermissions(contact?.permission);

  return { showprice, canorder, loading: isLoading };
};
